import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { Alert } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';

// Google client ID from environment
const GOOGLE_CLIENT_ID = (import.meta as unknown as { env: Record<string, string> }).env.VITE_GOOGLE_CLIENT_ID || '';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
  const sessionExpired = searchParams.get('session_expired') === 'true';

  // Google Sign-In callback
  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      await loginWithGoogle(response.credential);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
    } finally {
      setIsGoogleLoading(false);
    }
  }, [loginWithGoogle, navigate, from]);

  // Ref for measuring the Google button container width
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleScriptLoadedRef = useRef(false);
  const lastRenderedWidthRef = useRef(0);

  // Helper to render the Google button at the correct width
  const renderGoogleButton = useCallback(() => {
    const google = (window as unknown as { google?: { accounts: { id: { initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void; renderButton: (element: HTMLElement | null, config: { theme: string; size: string; width: number; shape: string; text: string }) => void } } } }).google;
    const container = googleButtonRef.current;
    if (!google || !container) return;

    // Measure container width and clamp to Google's allowed range (200-400)
    const containerWidth = Math.floor(container.getBoundingClientRect().width);
    const buttonWidth = Math.max(200, Math.min(400, containerWidth));

    // Skip re-render if width hasn't meaningfully changed (prevents infinite loop with ResizeObserver)
    if (Math.abs(buttonWidth - lastRenderedWidthRef.current) < 2) return;
    lastRenderedWidthRef.current = buttonWidth;

    // Clear previous render
    container.innerHTML = '';

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCallback,
    });
    google.accounts.id.renderButton(
      container,
      {
        theme: 'filled_black',
        size: 'large',
        width: buttonWidth,
        shape: 'rectangular',
        text: 'signin_with',
      }
    );
  }, [handleGoogleCallback]);

  // Load Google Identity Services script
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleScriptLoadedRef.current = true;
      renderGoogleButton();
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [renderGoogleButton]);

  // Re-render Google button on container resize for responsiveness
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const container = googleButtonRef.current;
    if (!container) return;

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (googleScriptLoadedRef.current) {
          renderGoogleButton();
        }
      }, 150);
    });
    observer.observe(container);
    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [renderGoogleButton]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login({
        email: formData.email,
        password: formData.password,
      });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <SEO
      title="Log In - Vettis"
      description="Log in to Vettis. Access your AI research dashboard and manage your research projects."
      path="/login"
    />
    <div className="verdict-card rounded-2xl p-8 sm:p-10 relative max-w-md w-full mx-auto">
      {/* Ambient glow behind card */}
      <div className="absolute -inset-24 pointer-events-none rounded-full bg-verdict-primary/5 blur-[100px]" />
      {/* Header */}
      <div className="text-center mb-8 relative">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-verdict-primary/10 border border-verdict-primary/20 mb-4 shadow-gold-md">
          <img src="/octoverdict-no-bg.png" alt="Vettis" className="w-9 h-9 object-contain" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-text-primary mb-1">Welcome back</h2>
        <p className="text-text-secondary text-sm">
          Sign in to your research console
        </p>
      </div>

      {sessionExpired && (
        <Alert variant="warning" className="mb-6">
          Your session has expired. Please log in again to continue.
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Google Sign-In */}
      {GOOGLE_CLIENT_ID && (
        <>
          <div className="mb-6 relative">
            {isGoogleLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 py-3 rounded-lg bg-surface-light border border-border-subtle text-text-secondary text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Signing in with Google...
              </div>
            )}
            <div
              ref={googleButtonRef}
              className={`w-full flex justify-center [&_iframe]:!w-full [&>div]:!w-full ${isGoogleLoading ? 'invisible' : ''}`}
            />
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-subtle" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4 py-0.5 bg-surface text-text-muted uppercase tracking-wider">or</span>
            </div>
          </div>
        </>
      )}

      {/* Email/Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1.5">Email</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="w-4 h-4 text-text-muted" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-1.5">Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="w-4 h-4 text-text-muted" />
            </div>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-4 py-2.5 bg-surface-light border border-border-subtle rounded-lg text-text-primary placeholder-text-muted text-sm focus:outline-none focus:border-verdict-primary/50 focus:ring-1 focus:ring-verdict-primary/30 transition-colors"
            />
          </div>
          <div className="mt-1.5 text-right">
            <Link to="/forgot-password" className="text-xs text-verdict-primary hover:text-verdict-primary-hover transition-colors">
              Forgot password?
            </Link>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 rounded-lg font-medium text-sm text-black bg-verdict-primary hover:bg-verdict-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Signing in...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-center text-text-muted text-sm mt-6">
        Don't have an account?{' '}
        <Link to="/signup" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors">
          Create one
        </Link>
      </p>
    </div>
    </>
  );
}

